/**
 * Asset Upload Hook
 * Handles file upload, Base64 conversion, icon selection, and preview state
 */

import { useState, useRef, ChangeEvent } from 'react'
import { log, LogCategory } from '../../../../../map-editor-shared/lib/logger'

interface AssetUploadState {
  assetFile: File | undefined
  previewUrl: string | null
  showIconSelector: boolean
}

interface FormIconUpdater {
  setIcon: (icon: string) => void
}

export function useAssetUpload(formUpdater: FormIconUpdater) {
  const [assetFile, setAssetFile] = useState<File | undefined>(undefined)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [showIconSelector, setShowIconSelector] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]

      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('이미지 파일만 업로드 가능합니다')
        return
      }

      // Warn about large files (base64 increases size by ~33%)
      const maxSize = 1024 * 1024 // 1MB
      if (file.size > maxSize) {
        log.warn(LogCategory.GENERAL, `Large file detected: ${(file.size / 1024).toFixed(0)}KB. Base64 encoding will increase size by ~33%`)
      }

      // Convert to base64 for localStorage persistence
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = reader.result as string
        setAssetFile(file)
        setPreviewUrl(base64String)
        formUpdater.setIcon(base64String)
        log.info(LogCategory.GENERAL, `Image converted to base64 (${(base64String.length / 1024).toFixed(0)}KB)`)
      }
      reader.onerror = () => {
        setError('이미지 변환 실패')
        log.error(LogCategory.GENERAL, 'Failed to convert image to base64')
      }
      reader.readAsDataURL(file)
    }
  }

  const removeFile = (e: React.MouseEvent) => {
    e.stopPropagation()
    setAssetFile(undefined)
    setPreviewUrl(null)
    formUpdater.setIcon('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleIconSelect = (path: string) => {
    formUpdater.setIcon(path)
    setPreviewUrl(path)
    setAssetFile(undefined)
    setShowIconSelector(false)
  }

  const resetAsset = () => {
    setAssetFile(undefined)
    setPreviewUrl(null)
  }

  const initPreviewFromIcon = (icon: string | undefined) => {
    if (icon && (
      icon.startsWith('data:image/') ||
      icon.startsWith('/assets/') ||
      icon.startsWith('http')
    )) {
      setPreviewUrl(icon)
    } else {
      setPreviewUrl(null)
    }
    setAssetFile(undefined)
  }

  return {
    assetFile,
    previewUrl,
    showIconSelector,
    setShowIconSelector,
    error: error,
    setError,
    fileInputRef,
    handleFileChange,
    removeFile,
    handleIconSelect,
    resetAsset,
    initPreviewFromIcon,
  }
}
